import { useEffect, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocalCurrency } from "@/hooks/useLocalCurrency";
import { X, Flame, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useGuestCheckout } from "@/hooks/useGuestCheckout";
import { trackConversion } from "@/lib/trackConversion";

const FLASH_SESSION_KEY = "flash_offer_shown";
const EXIT_DISMISSED_KEY = "exit_intent_dismissed";
const AB_VARIANT_KEY = "flash_ab_variant";

// A/B test: 50/50 split between €1.99 and €0.99
function getFlashVariant(): "A" | "B" {
  const stored = sessionStorage.getItem(AB_VARIANT_KEY);
  if (stored === "A" || stored === "B") return stored;
  const variant = Math.random() < 0.5 ? "A" : "B";
  sessionStorage.setItem(AB_VARIANT_KEY, variant);
  return variant;
}

const VARIANT_CONFIG = {
  A: { price: 1.99, originalPrice: 2.99, savings: "Save 33%", plan: "flash" as const },
  B: { price: 0.99, originalPrice: 2.99, savings: "Save 67%", plan: "flash_b" as const },
};

const FlashOfferModal = () => {
  const { isPremium } = useAuth();
  const { formatPriceWithEur } = useLocalCurrency();
  const { startCheckout, loading } = useGuestCheckout();
  const [show, setShow] = useState(false);
  const shownRef = useRef(sessionStorage.getItem(FLASH_SESSION_KEY) === "1");
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const [variant] = useState<"A" | "B">(() => getFlashVariant());
  const config = VARIANT_CONFIG[variant];

  // Calculate seconds until midnight local time
  const getSecondsUntilMidnight = () => {
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    return Math.max(0, Math.floor((midnight.getTime() - now.getTime()) / 1000));
  };

  const [countdown, setCountdown] = useState(getSecondsUntilMidnight);

  const triggerFlash = useCallback(() => {
    if (shownRef.current || isPremium) return;
    shownRef.current = true;
    sessionStorage.setItem(FLASH_SESSION_KEY, "1");
    setCountdown(getSecondsUntilMidnight());
    setShow(true);
    trackConversion("flash_offer_shown", undefined, { variant, price: config.price });
  }, [isPremium, variant, config.price]);

  // Listen for exit intent dismissal
  useEffect(() => {
    if (isPremium || shownRef.current) return;

    const check = () => {
      if (sessionStorage.getItem(EXIT_DISMISSED_KEY) === "1") {
        timerRef.current = setTimeout(triggerFlash, 20000);
      }
    };

    check();

    const handler = () => check();
    window.addEventListener("exit_intent_dismissed", handler);

    return () => {
      clearTimeout(timerRef.current);
      window.removeEventListener("exit_intent_dismissed", handler);
    };
  }, [isPremium, triggerFlash]);

  // Countdown timer
  useEffect(() => {
    if (!show) return;
    const interval = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(interval);
          setShow(false);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [show]);

  if (isPremium) return null;

  const hours = Math.floor(countdown / 3600);
  const minutes = Math.floor((countdown % 3600) / 60);
  const seconds = countdown % 60;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm p-4"
          onClick={() => setShow(false)}
        >
          <motion.div
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.85, opacity: 0 }}
            transition={{ type: "spring", stiffness: 250, damping: 22 }}
            className="relative max-w-md w-full rounded-2xl border border-destructive/40 bg-card p-6 shadow-2xl shadow-destructive/10"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShow(false)}
              className="absolute right-3 top-3 rounded-full p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="text-center">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/15">
                <Flame className="h-7 w-7 text-destructive" />
              </div>

              <p className="text-xs font-bold uppercase tracking-widest text-destructive">
                Last chance — today only
              </p>

              <h2 className="mt-2 text-2xl font-black text-foreground">
                {formatPriceWithEur(config.price)}
                <span className="text-base font-normal text-muted-foreground">/mo</span>
              </h2>

              <p className="mt-1 text-sm text-muted-foreground">
                <span className="line-through">{formatPriceWithEur(config.originalPrice)}/mo</span>
                {" — "}
                <span className="font-semibold text-destructive">{config.savings}</span>
              </p>

              <p className="mt-3 text-sm text-muted-foreground">
                Full premium access: real-time alerts, AI analysis, early warnings, and more.
              </p>

              <div className="mt-4 flex items-center justify-center gap-2 rounded-lg bg-destructive/10 px-4 py-2.5">
                <Clock className="h-4 w-4 text-destructive" />
                <span className="text-sm font-bold tabular-nums text-destructive">
                  Expires in {hours}:{minutes.toString().padStart(2, "0")}:{seconds.toString().padStart(2, "0")}
                </span>
              </div>

              <Button
                size="lg"
                variant="destructive"
                className="mt-4 w-full gap-2 font-bold text-base"
                disabled={loading}
                onClick={() => {
                  trackConversion("flash_offer_clicked", undefined, { variant, price: config.price });
                  startCheckout(config.plan);
                }}
              >
                <Flame className="h-4 w-4" />
                {loading ? "Redirecting…" : `Lock in ${formatPriceWithEur(config.price)}/mo`}
              </Button>
              <p className="mt-1.5 text-[10px] text-muted-foreground">
                Cancel anytime • Secure payment via Stripe
              </p>

              <button
                onClick={() => setShow(false)}
                className="mt-3 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                No thanks, I'll pass
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FlashOfferModal;
